/**
 * Spec 027 — TraceUp: saneamiento de PII y secretos en el frontend (research D5).
 *
 * DEBE mantenerse alineado con el backend
 * (apps/backend/src/common/traceup/pii-scrubber.ts) — invariante contracts §5.
 * Barrera inevadible: vive dentro del emisor (traceup.ts).
 */

/** Claves cuyo valor se redacta (match por substring, case-insensitive). */
export const DENYLIST_KEYS: string[] = [
  "authorization",
  "token",
  "access_token",
  "refresh_token",
  "accesstoken",
  "refreshtoken",
  "password",
  "clave",
  "clavesol",
  "usuariosol",
  "secret",
  "pfx",
  "privatekey",
  "private_key",
  "certificado",
  "certificate",
  "card",
  "tarjeta",
  "cvv",
  "culqi",
  "mercadopago",
  "apikey",
  "api_key",
  "cookie",
];

const REDACTED = "[REDACTED]";
const MASK = "***";
const MAX_DEPTH = 6;
const MAX_KEYS = 200;

const RE_JWT = /eyJ[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}\.[A-Za-z0-9_-]{5,}/g;
const RE_CARD = /\b(?:\d[ -]?){13,19}\b/g;
const RE_EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const RE_RUC = /\b\d{11}\b/g;
const RE_PHONE = /\b(?:\+?51)?9\d{8}\b/g;
const RE_DNI = /\b\d{8}\b/g;

export function scrubText(text: string): string {
  if (!text) return text;
  return text
    .replace(RE_JWT, MASK)
    .replace(RE_CARD, (m) =>
      /\d{13,19}/.test(m.replace(/[ -]/g, "")) ? MASK : m
    )
    .replace(RE_EMAIL, MASK)
    .replace(RE_RUC, MASK)
    .replace(RE_PHONE, MASK)
    .replace(RE_DNI, MASK);
}

function keyIsDenied(key: string): boolean {
  const k = key.toLowerCase();
  return DENYLIST_KEYS.some((denied) => k.includes(denied));
}

export function scrubValue(
  value: unknown,
  depth = 0,
  keyBudget = { n: 0 }
): unknown {
  if (value == null) return value;
  if (typeof value === "string") return scrubText(value);
  if (typeof value === "number" || typeof value === "boolean") return value;
  if (depth >= MAX_DEPTH) return "[TRUNCATED]";

  if (Array.isArray(value)) {
    return value.slice(0, 50).map((v) => scrubValue(v, depth + 1, keyBudget));
  }

  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (keyBudget.n >= MAX_KEYS) {
        out["[TRUNCATED]"] = true;
        break;
      }
      keyBudget.n += 1;
      out[k] = keyIsDenied(k) ? REDACTED : scrubValue(v, depth + 1, keyBudget);
    }
    return out;
  }

  return undefined;
}

export function truncate(text: string, max: number): string {
  if (!text) return text;
  return text.length > max
    ? `${text.slice(0, max)}…[+${text.length - max}]`
    : text;
}

/** Hash djb2 (corto, determinista) — mismo algoritmo que el backend. */
export function djb2(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
}
